import { type ChangeEvent, type KeyboardEvent, type MouseEvent, type Ref, type RefObject } from 'react'

import { ButtonDefault, ButtonRoot } from '@/components/common/Button'
import { LoadingMask } from '@/components/common/LoadingMask'
import { Icon } from '@/components/icons/Icon'
import { type IconType } from '@/defs/icon'
import { type TypeaheadOptionType } from '@/defs/input'
import { cn } from '@/utils/commonUtil'

import styles from '../InputTypeaheadContent.module.scss'

export type Props<T extends string = string> = {
  'aria-label'?: string
  autoFocus?: boolean
  disabled?: boolean
  icon: IconType
  isEmpty: boolean
  isOpen: boolean
  multiselect?: boolean
  onClear?: () => void
  onKeyDownInput: (e: KeyboardEvent<HTMLInputElement>) => void
  onOpen: () => void
  onRemoveLast: () => void
  onSelect: (selected: T) => void
  placeholder?: string
  ref?: Ref<HTMLInputElement>
  refInput: RefObject<HTMLInputElement | null>
  required?: boolean
  search: string
  selections: TypeaheadOptionType<T>[]
  setIsOpen: (isOpen: boolean) => void
  setSearch: (search: string) => void
  showClear?: boolean
  showLoadingMask?: boolean
  values: T[]
  variant?: 'input' | 'trigger'
}

export const InputTypeaheadContentField = <T extends string = string>({
  'aria-label': ariaLabel,
  autoFocus,
  disabled,
  icon,
  isEmpty,
  isOpen,
  multiselect,
  onClear,
  onKeyDownInput,
  onOpen,
  onRemoveLast,
  onSelect,
  placeholder,
  ref,
  refInput,
  required,
  search,
  selections,
  setIsOpen,
  setSearch,
  showClear = true,
  showLoadingMask,
  values,
  variant = 'input',
}: Props<T>) => {
  const isTrigger = variant === 'trigger'

  const [selection] = selections
  const showSelection = !multiselect && !isEmpty && !isOpen

  const onFocus = () => {
    if (disabled) return
    if (!isEmpty) setSearch('')
    setIsOpen(true)
  }

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (disabled) return
    setSearch(e.currentTarget.value)
    setIsOpen(true)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (multiselect && e.key === 'Backspace' && !search) {
      e.preventDefault()
      if (!e.repeat) onRemoveLast()
      return
    }
    onKeyDownInput(e)
  }

  const onClickInput = () => {
    if (!disabled) setIsOpen(true)
  }

  const onMouseDownField = (e: MouseEvent<HTMLDivElement>) => {
    if (isTrigger || disabled || e.target !== e.currentTarget) return
    e.preventDefault()
    refInput.current?.focus()
    setIsOpen(true)
  }

  const onClickClear = () => {
    if (disabled) return
    setSearch('')
    if (isEmpty) {
      refInput.current?.focus()
      return
    }
    if (!multiselect) setIsOpen(false)
    onClear?.()
  }

  const onClickIcon = () => refInput.current?.focus()

  const showSearchIcon = !showClear || (isEmpty && !search)

  const renderSelection = ({ Component, label, labelDisplay, value }: TypeaheadOptionType<T>) => {
    const text = labelDisplay || label || value
    return Component ? (
      <Component>
        <span>{text}</span>
      </Component>
    ) : (
      text
    )
  }

  const renderRemove = (option: TypeaheadOptionType<T>) =>
    isTrigger ? (
      <span aria-hidden className={styles.selectionRemove}>
        <Icon name='xMark' />
      </span>
    ) : (
      <ButtonRoot
        aria-label={`Remove ${option.label || option.value}`}
        className={styles.selectionRemove}
        disabled={disabled}
        onClick={() => onSelect(option.value)}
      >
        <Icon name='xMark' />
      </ButtonRoot>
    )

  return (
    <>
      {required && (
        <input
          aria-hidden
          aria-label='Required field'
          className={styles.requiredInput}
          onChange={() => null}
          required
          tabIndex={-1}
          value={values.join(',')}
        />
      )}
      {multiselect && selections.length > 0 && (
        <div className={styles.selections} onMouseDown={onMouseDownField}>
          {selections.map((option) => (
            <ButtonDefault
              as='div'
              colorScheme='selected'
              fontNormal
              hover='none'
              justifyContent='left'
              key={option.value}
              size='xs'
              whitespace='wrap'
            >
              <span>{renderSelection(option)}</span>
              {renderRemove(option)}
            </ButtonDefault>
          ))}
        </div>
      )}
      <div className={styles.row}>
        <div className={styles.field} onMouseDown={onMouseDownField}>
          {!isTrigger && (
            <input
              aria-label={ariaLabel}
              autoComplete='off'
              autoFocus={autoFocus}
              className={cn(styles.input, { showSelection, styles })}
              onChange={onChange}
              onClick={onClickInput}
              onFocus={onFocus}
              onKeyDown={onKeyDown}
              placeholder={isEmpty ? placeholder : ''}
              ref={ref}
              type='search'
              value={search}
            />
          )}
          {isTrigger && isEmpty && <span className={styles.placeholder}>{placeholder}</span>}
          {!multiselect && selection && (
            <div className={cn(styles.selection, { showSelection, styles })}>
              <ButtonDefault as='div' colorScheme='selected' fontNormal size='sm'>
                {renderSelection(selection)}
              </ButtonDefault>
            </div>
          )}
        </div>
        {isTrigger ? (
          <span className={styles.icon}>
            <Icon name={icon} />
          </span>
        ) : showSearchIcon ? (
          <ButtonRoot aria-label='Search' className={styles.icon} onClick={onClickIcon}>
            <Icon name={icon} />
          </ButtonRoot>
        ) : (
          <ButtonRoot aria-label='Clear' className={styles.icon} onClick={onClickClear}>
            <Icon name='xMark' />
          </ButtonRoot>
        )}
      </div>
      {isTrigger && (
        <ButtonRoot aria-label={placeholder || 'Open Typeahead'} className={styles.button} onClick={onOpen} />
      )}
      <LoadingMask loading={showLoadingMask} />
    </>
  )
}
